import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AdFilters, RequestParams} from "../../api/RequestParams";
import {AdvertApiResponse} from "../../types/ApiResponse";
import {Advert} from "../../types/Advert";
import {fetchAds} from "../actions/fetchAds";
import {sumKeyValues} from "../../utils/sumKeyValues";
import {act} from "react-dom/test-utils";

export interface SearchResultState {
    adsLoading: boolean;
    sortField: string,
    currentPage: number,
    pageSize: number,
    totalPages: number,
    resultCount: number,
    cachedResponse: AdvertApiResponse<Advert> | null;
    filters: AdFilters
}

const urlSearchParams = new URLSearchParams(window.location.search);
const params = Object.fromEntries(urlSearchParams.entries());

let tmpResult: AdvertApiResponse<Advert> | null = null;
try {
    tmpResult = JSON.parse(localStorage.getItem("tmp-search-result") || "");
} catch {
}

const initialState: SearchResultState = {
    adsLoading: false,
    sortField: params.sort ? params.sort : "latest-cars",
    currentPage: params.number ? +params.number : 1,
    pageSize: params.size ? +params.size : 8,
    totalPages: tmpResult ? tmpResult?.meta.pagination.total_pages : 1,
    resultCount: tmpResult ? sumKeyValues(tmpResult.meta.filters.make) : 0,
    cachedResponse: tmpResult,
    filters: {
        make: params.make ? params.make.replace("-", " ") : undefined,
        model: params.model ? params.model.replace("-", " ") : undefined,
        colour: params.colour,
        "body-style": params["body-style"] ? params["body-style"].replace("-", " ") : undefined,
        "fuel-type": params["fuel-type"],
        price: params.price ? +params.price : undefined,
        doors: params.doors ? +params.doors : undefined,
        transmission: params.transmission,
        "geo.location": params["geo.location"] || params["geo_location"],
        "geo.miles": params["geo.miles"] ? +params["geo.miles"] : undefined,
        "geo.postcode": params["geo.postcode"] || params["geo_postcode"],
    }
};

const searchResultSlice = createSlice({
    name: "searchResult",
    initialState,
    reducers: {
        initStateFromQueryParams: (state: SearchResultState, action: PayloadAction<RequestParams>) => {
            state.filters = {...action.payload.filters};
            state.pageSize = action.payload.pagination.size!;
            state.currentPage = action.payload.pagination.number!;
            state.sortField = action.payload.sort.field!;
        },
        initAdsFromCache: (state: SearchResultState, action: PayloadAction) => {
            if (state.cachedResponse) {
                state.currentPage = 1;
                state.resultCount = sumKeyValues(state.cachedResponse.meta.filters.make);
                state.totalPages = state.cachedResponse.meta.pagination.total_pages;
            }
        },
        setFilters: (state: SearchResultState, action: PayloadAction<AdFilters>) => {
            state.filters = {...action.payload};
        },
        setCurrentPage: (state: SearchResultState, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        setPageSize: (state: SearchResultState, action: PayloadAction<number>) => {
            const newTotalPages = Math.round((state.totalPages * state.pageSize) / action.payload);
            if (state.currentPage > newTotalPages) {
                state.currentPage = newTotalPages;
                state.totalPages = newTotalPages;
            }
            state.pageSize = action.payload;
        },
        setSortField: (state: SearchResultState, action: PayloadAction<string>) => {
            state.sortField = action.payload;
        },
        setCachedResponse: (state: SearchResultState, action: PayloadAction<AdvertApiResponse<Advert>>) => {
            state.cachedResponse = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAds.pending, (state) => {
                state.adsLoading = true;
            })
            .addCase(fetchAds.fulfilled, (state, action) => {
                const res = action.payload;
                state.adsLoading = false;
                state.cachedResponse = res;
                state.resultCount = sumKeyValues(res.meta.filters.make);
                state.totalPages = res.meta.pagination.total_pages;
            })
            .addCase(fetchAds.rejected, (state, action) => {
                // state.isError = true;
                state.adsLoading = false;
            });
    },
});

export const {
    initAdsFromCache,
    setCurrentPage,
    setPageSize,
    setSortField,
    initStateFromQueryParams,
    setFilters,
    setCachedResponse
} = searchResultSlice.actions;
export default searchResultSlice.reducer;